import React, { useState } from "react";

interface CheckItem {
  id: string;
  label: string;
  status: string;
  note: string;
}

export interface MedCheckRecord {
  id: string;
  shift: string;
  date: string;
  checker: string;
  checkerRole: string;
  items: CheckItem[];
  actionPlan: string;
  savedAt: string;
}

const STORE_KEY = "med_check_records";

const ITEM_LABELS: string[] = [
  "All MAR entries signed up to time with no unexplained gaps",
  "Refusals recorded, re-offered and actioned",
  "Missed doses followed up and documented",
  "PRN given as directed with effect recorded",
  "Creams and eye drops applied and signed",
  "Controlled drugs book balanced with two staff signatures and safe locked",
  "Medicines cabinet locked and keys controlled",
  "Medicines fridge temperature 2 to 8 C recorded",
  "Emergency medicines box stock checked and in date",
  "External medicines labelled with resident name",
  "Pharmacy deliveries received and checked against the order",
  "Staff on shift are trained and assessed as competent to administer medicines",
];

function freshItems(): CheckItem[] {
  return ITEM_LABELS.map((label, index) => ({ id: `mc-${index + 1}`, label, status: "", note: "" }));
}

function loadHistory(): MedCheckRecord[] {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "[]");
  } catch (error) {
    return [];
  }
}

export default function MedicationChecks({ user }: { user?: any }) {
  const [shift, setShift] = useState("Early");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [checker, setChecker] = useState(user && user.name ? user.name : "");
  const [checkerRole, setCheckerRole] = useState(user && user.role ? user.role : "");
  const [items, setItems] = useState<CheckItem[]>(freshItems);
  const [actionPlan, setActionPlan] = useState("");
  const [history, setHistory] = useState<MedCheckRecord[]>(loadHistory);
  const [showHistory, setShowHistory] = useState(false);
  const [message, setMessage] = useState("");

  const updateItem = (id: string, patch: Partial<CheckItem>) => {
    setItems(current => current.map(item => (item.id === id ? { ...item, ...patch } : item)));
  };

  const failedCount = items.filter(item => item.status === "fail").length;

  const saveCheck = () => {
    if (!checker) {
      setMessage("Enter the checker name before saving.");
      return;
    }

    const unchecked = items.filter(item => !item.status).length;

    if (unchecked > 0) {
      setMessage(`${unchecked} checks still unanswered. Mark every item pass or fail.`);
      return;
    }

    const record: MedCheckRecord = {
      id: `medcheck-${Date.now()}`,
      shift,
      date,
      checker,
      checkerRole,
      items,
      actionPlan: failedCount > 0 && !actionPlan ? "See failed items; actions to be recorded in Action Plans." : actionPlan,
      savedAt: new Date().toISOString(),
    };

    const next = [record, ...history];

    setHistory(next);
    localStorage.setItem(STORE_KEY, JSON.stringify(next));
    setMessage(`Medication check saved with ${failedCount} failed items. Failed items now appear in Action Plans.`);
    setItems(freshItems());
    setActionPlan("");
  };
  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Medication Checks</h2>
          <p className="text-sm text-slate-600">MAR and medicines audit at every shift change. Every failed item becomes an action plan automatically.</p>
        </div>
        <button onClick={() => setShowHistory(!showHistory)} className="rounded-lg border border-blue-900 px-4 py-2 text-sm font-medium text-blue-900">
          {showHistory ? "Back to check" : `History (${history.length})`}
        </button>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      {showHistory ? (
        <div className="space-y-3">
          {history.map(record => (
            <div key={record.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-medium text-slate-900">{record.shift} shift - {record.date}</div>
                <div className="text-sm text-slate-600">{record.checker} ({record.checkerRole})</div>
              </div>
              <div className="mt-1 text-xs text-slate-500">
                Saved {new Date(record.savedAt).toLocaleString()} | Failed items {record.items.filter(item => item.status === "fail").length}
              </div>
              {record.items.filter(item => item.status === "fail").length > 0 ? (
                <div className="mt-2 space-y-1">
                  {record.items.filter(item => item.status === "fail").map(item => (
                    <div key={item.id} className="text-xs text-red-700">FAIL: {item.label}{item.note ? ` - ${item.note}` : ""}</div>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
          {history.length === 0 ? <div className="text-sm text-slate-500">No medication checks recorded yet.</div> : null}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <select value={shift} onChange={event => setShift(event.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
              <option>Early</option>
              <option>Late</option>
              <option>Night</option>
            </select>
            <input type="date" value={date} onChange={event => setDate(event.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <input value={checker} onChange={event => setChecker(event.target.value)} placeholder="Checker name" className="w-44 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <input value={checkerRole} onChange={event => setCheckerRole(event.target.value)} placeholder="Role" className="w-40 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="space-y-4">
              {items.map(item => (
                <div key={item.id} className="rounded-lg border border-slate-100 p-3">
                  <div className="text-sm font-medium text-slate-800">{item.label}</div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <button onClick={() => updateItem(item.id, { status: "pass" })} className={item.status === "pass" ? "rounded-lg bg-green-700 px-3 py-1 text-sm text-white" : "rounded-lg border border-slate-300 px-3 py-1 text-sm text-slate-700"}>Pass</button>
                    <button onClick={() => updateItem(item.id, { status: "fail" })} className={item.status === "fail" ? "rounded-lg bg-red-700 px-3 py-1 text-sm text-white" : "rounded-lg border border-slate-300 px-3 py-1 text-sm text-slate-700"}>Fail</button>
                    <input value={item.note} onChange={event => updateItem(item.id, { note: event.target.value })} placeholder="Note or action taken" className="flex-1 rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-2 font-semibold text-slate-900">Shift action plan for any failures</h3>
            <textarea value={actionPlan} onChange={event => setActionPlan(event.target.value)} rows={3} placeholder="What will be done, by whom and by when for any failed item" className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          </div>

          <button onClick={saveCheck} className="rounded-lg bg-blue-900 px-5 py-2 text-sm font-medium text-white">
            Save medication check
          </button>
        </div>
      )}
    </div>
  );
}
